import { api, ApiError } from '../net/api';

/**
 * The one place that knows whether sound and music are on.
 *
 * Sounds are played from several places -- a cash-out, a crash, a punch landing --
 * and each of those asking the server, or reading localStorage, or worse, keeping
 * its own copy of the setting, is how a "mute" button ends up muting three of five
 * sounds. So this module is the only thing that knows, and `audio/player.ts`
 * (which does the playing) asks it and subscribes to it rather than storing
 * anything.
 *
 * The account is the source of truth (the handshake carries it), so a player who
 * turns the music off on a phone does not hear it on a laptop. localStorage is a
 * cache for the frames before the handshake answers, nothing more: on a
 * disagreement the server wins.
 */

interface AudioState {
  sound: boolean;
  music: boolean;
}

const STORAGE_KEY = 'fighter.audio';

let state: AudioState = readCache() ?? { sound: true, music: true };
const listeners = new Set<(s: AudioState) => void>();

export function isSoundEnabled(): boolean {
  return state.sound;
}

export function isMusicEnabled(): boolean {
  return state.music;
}

export function audioState(): AudioState {
  return { ...state };
}

/** Registers a callback for whoever ends up owning playback. */
export function onAudioChange(listener: (s: AudioState) => void): void {
  listeners.add(listener);
}

/** Applies what the handshake said, without sending it back. */
export function applyServerAudio(sound: boolean, music: boolean): void {
  state = { sound, music };
  writeCache();
  emit();
}

/**
 * Changes a setting and stores it on the account.
 *
 * The switch moves before the request is answered -- a toggle that waits for the
 * network feels broken -- but a refusal puts it back and tells the caller, rather
 * than leaving the interface claiming something the account does not say.
 */
export async function setAudio(next: Partial<AudioState>): Promise<void> {
  const previous = state;
  state = { ...state, ...next };
  writeCache();
  emit();

  try {
    await api.setPreferences(state.sound, state.music);
  } catch (e) {
    state = previous;
    writeCache();
    emit();
    throw e instanceof ApiError ? e : new Error('audio: the setting could not be saved');
  }
}

function emit(): void {
  for (const listener of listeners) listener({ ...state });
}

function readCache(): AudioState | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { sound, music } = parsed as Partial<AudioState>;
    return { sound: sound !== false, music: music !== false };
  } catch {
    // Private browsing can throw on access; defaults are fine.
    return null;
  }
}

function writeCache(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* see readCache */
  }
}
